<template>
  <div class="blog-wrapper">
    <b-overlay
        :show="isLoading"
        rounded="sm"
    >
      <b-row class="blog-list-wrapper">
        <b-card no-body class="col-md-12">
          <b-card-header class="pb-50">
            <h5>Поиск Каверов</h5>
          </b-card-header>
          <b-card-body>
            <b-row class="mb-1">
              <b-col cols="12">
                <b-form-radio-group
                    v-model="mode"
                    :options="modeOptions"
                    buttons
                    button-variant="outline-primary"
                    size="sm"
                />
              </b-col>
            </b-row>
            <b-row>
              <b-col cols="10" md="10" class="mb-md-0 mb-2">
                <b-form-input
                    v-if="mode === 'yandex' || mode === 'spotify'"
                    v-model="playlistUrl"
                    placeholder="https://..."
                    @keyup.enter="parsePlaylist"
                />
                <b-form-textarea
                    v-if="mode === 'text'"
                    v-model="tracklistText"
                    placeholder="Artist - Title (каждый трек на новой строке)"
                    rows="5"
                />
              </b-col>
              <b-col cols="2" md="2">
                <b-button
                    variant="primary"
                    @click="parsePlaylist"
                    :disabled="isLoading"
                >
                  Найти
                </b-button>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <!-- Playlist info -->
        <b-card
            v-if="playlistInfo"
            no-body
            class="col-md-12"
        >
          <b-card-body>
            <h4 v-if="playlistInfo.title">{{ playlistInfo.title }}</h4>
            <p v-if="playlistInfo.description" v-html="playlistInfo.description" />
            <span v-if="playlistInfo.count">Треков: {{ playlistInfo.count }}</span>
          </b-card-body>
        </b-card>

        <!-- Results table -->
        <b-card
            v-if="tracks.length > 0"
            no-body
            class="col-md-12"
        >
          <b-table
              :items="tracks"
              responsive
              :fields="tableColumns"
              primary-key="index"
              show-empty
              empty-text="Нет треков"
              class="position-relative"
              :tbody-tr-class="rowClass"
          >
            <template #cell(row_num)="data">
              {{ data.index + 1 }}
            </template>

            <template #cell(artists)="data">
              <span v-for="(artist, index) in data.item.artists" :key="index">
                <b-link
                    v-if="artist.id"
                    :to="{ name: 'artist-view', params: { id: artist.id }}"
                    class="font-weight-bold"
                >
                  {{ artist.name }}
                </b-link>
                <span v-else>{{ artist.name }}</span>
                <span v-if="index < data.item.artists.length - 1">, </span>
              </span>
            </template>

            <template #cell(title)="data">
              <b-link
                  v-if="data.item.id"
                  :to="{ name: 'track-detail', params: { id: data.item.id }, query: { tab: 'covers' }}"
                  class="font-weight-bold"
              >
                {{ data.item.title }}
              </b-link>
              <span v-else>{{ data.item.title }}</span>
              <div v-if="data.item.comment" class="text-muted" style="font-size: 0.85em; font-style: italic;">{{ data.item.comment }}</div>
              <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  v-if="!data.item.id"
                  variant="warning"
                  class="btn-icon ml-50"
                  size="sm"
                  :disabled="rowStates[data.index].isAddingTrack"
                  @click="addTrackToDb(data.index)"
              >
                <feather-icon icon="PlusIcon" />
              </b-button>
            </template>

            <template #cell(original)="data">
              <div v-if="data.item.original_title && !rowStates[data.index].isEditing" class="d-flex align-items-center">
                <span>{{ data.item.original_title }}</span>
                <b-button
                    variant="flat-primary"
                    class="btn-icon ml-50 p-25"
                    size="sm"
                    @click="rowStates[data.index].isEditing = true"
                >
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
              </div>
              <v-select
                  v-else
                  v-model="rowStates[data.index].selectedOriginal"
                  :options="rowStates[data.index].searchOptions"
                  label="title"
                  :filterable="false"
                  :loading="rowStates[data.index].isSearching"
                  @search="(search, loading) => onSearch(search, loading, data.index)"
                  style="min-width: 300px"
              >
                <template #option="option">
                  <span>{{ option.title }}</span>
                  <b-badge v-if="option.is_cover" variant="light-warning" class="ml-50">cover</b-badge>
                  <b-badge v-if="option.has_covers" variant="light-success" class="ml-50">original</b-badge>
                </template>
                <template #no-options>
                  Введите для поиска...
                </template>
              </v-select>
            </template>

            <template #cell(actions)="data">
              <span class="text-nowrap">
                <!-- Save original -->
                <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    :variant="rowStates[data.index].isSaved ? 'success' : 'primary'"
                    class="btn-icon"
                    size="sm"
                    :disabled="!data.item.id || !rowStates[data.index].selectedOriginal || rowStates[data.index].isSaving"
                    @click="saveOriginal(data.index)"
                >
                  <feather-icon :icon="rowStates[data.index].isSaved ? 'CheckIcon' : 'SaveIcon'" />
                </b-button>

                <!-- Google search -->
                <b-button
                    :href="googleSearchUrl(data.item)"
                    variant="info"
                    class="btn-icon ml-50"
                    size="sm"
                    target="_blank"
                >
                  <feather-icon icon="GlobeIcon" />
                </b-button>
              </span>
            </template>
          </b-table>
        </b-card>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BFormInput,
  BFormTextarea,
  BFormRadioGroup,
  BButton,
  BTable,
  BOverlay,
  BLink,
  BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import Vue from 'vue'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BFormInput,
    BFormTextarea,
    BFormRadioGroup,
    BButton,
    BTable,
    BOverlay,
    BLink,
    BBadge,
    vSelect,
  },
  directives: {
    Ripple,
  },
  setup() {
    const STORE_MODULE_NAME = 'covers'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const mode = ref('yandex')
    const playlistUrl = ref('')
    const tracklistText = ref('')
    const isLoading = ref(false)
    const playlistInfo = ref(null)
    const tracks = ref([])
    const rowStates = ref([])

    const modeOptions = [
      { text: 'Yandex', value: 'yandex' },
      { text: 'Spotify', value: 'spotify' },
      { text: 'Список треков', value: 'text' },
    ]

    const tableColumns = [
      { key: 'row_num', label: '№', sortable: false },
      { key: 'artists', sortable: false },
      { key: 'title', sortable: false },
      { key: 'original', label: 'Оригинал', sortable: false },
      { key: 'actions', label: 'Действия', sortable: false },
    ]

    const parsePlaylist = async () => {
      isLoading.value = true
      playlistInfo.value = null
      tracks.value = []
      rowStates.value = []

      const payload = { source: mode.value }
      if (mode.value === 'text') {
        payload.text = tracklistText.value
      } else {
        payload.link = playlistUrl.value
      }

      try {
        const response = await store.dispatch('covers/coverImport', payload)
        const data = response.data

        if (data.title || data.description || data.count) {
          playlistInfo.value = {
            title: data.title,
            description: data.description,
            count: data.count,
          }
        }

        tracks.value = data.tracks || []
        rowStates.value = tracks.value.map(track => ({
          selectedOriginal: null,
          isEditing: false,
          isSaving: false,
          isSaved: false,
          isAddingTrack: false,
          isSearching: false,
          searchOptions: (track.search_results || []).map(sr => ({
            id: sr.id,
            title: sr.title,
            artist_id: sr.artist_id,
            artist_name: sr.artist_name,
            is_cover: sr.is_cover,
            has_covers: sr.has_covers,
          })),
        }))
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    let searchTimers = {}
    const onSearch = (search, loading, rowIndex) => {
      if (!search || search.length < 2) return

      if (searchTimers[rowIndex]) clearTimeout(searchTimers[rowIndex])

      searchTimers[rowIndex] = setTimeout(async () => {
        rowStates.value[rowIndex].isSearching = true
        loading(true)
        try {
          const searchParams = { q: search, limit: 15 }
          const trackId = tracks.value[rowIndex].id
          if (trackId) searchParams.exclude_id = trackId
          const response = await store.dispatch('covers/searchTracksInDb', searchParams)
          const items = response.data.data || response.data || []
          const results = items.map(item => ({
            id: item.id,
            title: item.title,
            artist_name: item.artist || item.artist_name,
            is_cover: item.is_cover,
            has_covers: item.has_covers,
          }))

          rowStates.value[rowIndex].searchOptions = results
        } catch (error) {
          // silent
        } finally {
          rowStates.value[rowIndex].isSearching = false
          loading(false)
        }
      }, 1000)
    }

    const ensureTrackInDb = async (rowIndex) => {
      const track = tracks.value[rowIndex]
      if (track.id) return track.id

      let artistId = null
      if (track.artists && track.artists[0]) {
        if (track.artists[0].id) {
          artistId = track.artists[0].id
        } else if (track.artists[0].name) {
          const res = await store.dispatch('covers/createArtist', { title: track.artists[0].name, is_cover_artist: true, skip_originals: true })
          artistId = res.data.data.id
          track.artists[0].id = artistId
        }
      }

      if (artistId) {
        const res = await store.dispatch('covers/addTrack', { id: artistId, queryParams: { title: track.title } })
        const newTrackId = res.data.track_id || (res.data.data ? res.data.data.id : res.data.id)
        Vue.set(track, 'id', newTrackId)
        return newTrackId
      }

      return null
    }

    const saveOriginal = async (rowIndex) => {
      const state = rowStates.value[rowIndex]
      if (!state.selectedOriginal) return

      state.isSaving = true
      state.isSaved = false

      try {
        const trackId = await ensureTrackInDb(rowIndex)
        if (!trackId) {
          throw new Error('Не удалось создать трек')
        }

        await store.dispatch('covers/setTrackOriginal', {
          id: trackId,
          original_id: state.selectedOriginal.id,
        })

        state.isSaved = true
        state.isEditing = false
        Vue.set(tracks.value[rowIndex], 'original_track_id', state.selectedOriginal.id)
        Vue.set(tracks.value[rowIndex], 'original_title', state.selectedOriginal.title)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Оригинал сохранён',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка сохранения',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        state.isSaving = false
      }
    }

    const addTrackToDb = async (rowIndex) => {
      const state = rowStates.value[rowIndex]
      state.isAddingTrack = true

      try {
        await ensureTrackInDb(rowIndex)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек добавлен в БД',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        state.isAddingTrack = false
      }
    }

    const googleSearchUrl = (track) => {
      const artist = track.artists && track.artists[0] ? track.artists[0].name : ''
      const query = `${artist} ${track.title} оригинал песни кавер чья песня`
      return `https://www.google.com/search?q=${encodeURIComponent(query)}`
    }

    const rowClass = (item) => {
      if (!item) return ''
      if (item.original_track_id) return 'table-warning'
      if (item.has_covers) return 'table-success'
      return ''
    }

    return {
      mode,
      modeOptions,
      playlistUrl,
      tracklistText,
      isLoading,
      playlistInfo,
      tracks,
      rowStates,
      tableColumns,
      parsePlaylist,
      onSearch,
      saveOriginal,
      addTrackToDb,
      googleSearchUrl,
      rowClass,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';
</style>
